<template>
  <div>
    <b-field class="fob-entity-list-bar" grouped group-multiline style="margin-bottom: 50px">
      <fob-entity-palette v-model="resourceType"></fob-entity-palette>

      <b-select v-model="perPage">
        <option value="5">5 <translate>list.header.perPage</translate></option>
        <option value="10">10 <translate>list.header.perPage</translate></option>
        <option value="15">15 <translate>list.header.perPage</translate></option>
        <option value="20">20 <translate>list.header.perPage</translate></option>
        <option value="100">100 <translate>list.header.perPage</translate></option>
      </b-select>
      <fob-entity-list-column-visibility
        class="control"
        v-model="tableFields"
      ></fob-entity-list-column-visibility>
      <button @click="saveView" class="control button is-primary">
        <b-icon pack="fa" icon="save"></b-icon>
        <translate>list.button.save</translate>
      </button>
      <button @click="print" class="control button is-primary">
        <b-icon pack="fa" icon="print"></b-icon>
        <translate>list.button.print</translate>
      </button>
    </b-field>

    <fob-entity-list-options
      class="fob-entity-list-options"
      v-model="filter"
      :tableFields="tableFields"
      :filterSchema="filterSchema"
      :page="page"
      :per-page="perPage"
      :total="total"
    ></fob-entity-list-options>

    <div class="card">
      <!-- PAGINATION BEFORE COMPACT LIST / TABLE -->
      <fob-entity-list-pagination
        @update:current="onPageChange"
        @update:compact="updateCompact"
        :per-page="perPage"
        :page="page"
        :total="total"
        :compact="isCompact"
      >
      </fob-entity-list-pagination>

      <template v-if="total > 0">
        <!-- LIST OF COMPACTED ENTITIES -->
        <div v-if="isCompact" class="fob-entity-compact-view">
          <fob-entity-list-compact
            :data="data"
            :schema="schema"
            :loading="loading"
            entityDetailsRoute="anzeigen_einzeln"
            entityEditRoute="editieren"
          ></fob-entity-list-compact>
        </div>

        <!-- TABLE OF ENTITIES -->
        <div v-else class="fob-entity-list">
          <fob-entity-list-table
            :tableFields="tableFields"
            :data="data"
            :loading="loading"
            :key="resourceType"
            :sortOrder="sortOrder"
            :sortField="sortField"
            entityDetailsRoute="anzeigen_einzeln"
            entityEditRoute="editieren"
            @updateSorting="updateSorting"
          >
          </fob-entity-list-table>
        </div>
      </template>
      <div v-else>
        <div class="level">
          <div class="level-item has-text-centered">
            <b-icon icon="exclamation" type="is-garnet"></b-icon>
            <em><translate>list.noData</translate></em>
          </div>
        </div>
      </div>

      <!-- PAGINATION AFTER COMPACT LIST / TABLE -->
      <fob-entity-list-pagination
        @update:current="onPageChange"
        @update:compact="updateCompact"
        :per-page="perPage"
        :page="page"
        :total="total"
        :compact="isCompact"
      >
      </fob-entity-list-pagination>
    </div>

    <div class="print-footer">
      <span>
        <translate :translate-params="{printDate: printDate}">list.printedOn</translate>
      </span>
    </div>
  </div>
</template>

<script>
// UTILS
import api from '@/api'
import { ToastSuccess } from '@/utils'
import { mapGetters } from 'vuex'
import store from '@/store'
import debounce from 'debounce-promise'
// COMPONENTS
import EntityListColumnVisibility from '@/components/EntityListColumnVisibility'
import EntityListOptions from '@/components/EntityListOptions'
import EntityListCompact from '@/components/EntityListCompact'
import EntityListTable from '@/components/EntityListTable'
import EntityPalette from '@/components/EntityPalette'
import EntityListPagination from '@/components/EntityListPagination'
// MIXINS
import DynamicTitle from '@/components/mixins/DynamicTitle'
import DateTimeHelper from '@/components/mixins/DateTimeHelper'

export default {
  name: 'fob-entity-list-view',
  mixins: [DynamicTitle, DateTimeHelper],
  data () {
    return {
      data: [],
      total: 0,
      loading: false,
      showConfig: false,
      isCompact: false,
      printDate: ''
    }
  },
  computed: {
    ...mapGetters({
      page: 'list/getPage'
    }),
    leurisHead () {
      let msgid = this.resourceType.charAt(0).toUpperCase() + this.resourceType.slice(1)
      return {
        inner: 'leuris:Forschungsbericht',
        separator: '|',
        complement: ['menu.list', msgid]
      }
    },
    filter: {
      get () {
        return this.$store.getters['list/getFilter']
      },
      set (newFilter) {
        this.$store.dispatch('list/updateFilter', newFilter)
        this.refreshTableAfterFilterUpdate()
      }
    },
    filterSchema () {
      return this.$store.getters['list/getFilterSchema']
    },
    schema () {
      return this.$store.getters['list/getSchema']
    },
    tableFields: {
      get () {
        return this.schema.fields
      },
      set (newListSchemaFields) {
        this.$store.dispatch('list/updateListSchemaFields', newListSchemaFields)
      }
    },
    resourceType: {
      get () {
        return this.$store.getters['list/getResourceType']
      },
      set (newType) {
        this.data = []
        this.$router.push({
          name: 'auflisten_resource',
          params: {
            resourceType: newType
          }
        })
        this.refreshTableAfterPaging()
      }
    },
    perPage: {
      get () {
        return this.$store.getters['list/getPerPage']
      },
      set (perPage) {
        this.$store
          .dispatch('list/setPerPage', perPage)
          .then(this.$nextTick(this.refreshTableAfterPaging))
      }
    },
    sortField: {
      get () {
        return this.$store.getters['list/getSortField']
      },
      set (sortField) {
        this.$store.dispatch('list/setSortField', sortField)
      }
    },
    sortOrder: {
      get () {
        return this.$store.getters['list/getSortOrder']
      },
      set (order) {
        this.$store.dispatch('list/setSortOrder', order)
      }
    },
    filterParams () {
      // value 'null' and empty strings as well as key '_dirty' will be removed,
      // but value 'false' is explicitly used for filtering
      // (necessary for e.g. filter 'hatPreisgeld' on awards)
      let keys = Object.keys(this.filter).filter((x) => {
        let blackListFilterParams = {
          keys: ['_dirty'],
          values: ['', null]
        }
        return !blackListFilterParams.keys.includes(x) &&
          !blackListFilterParams.values.includes(this.filter[x])
      })
      // generate param object for query parameters
      let params = {}
      for (let x of keys) {
        let f = this.filter[x]
        if (f !== null && typeof f === 'object') {
          params[x] = f._id
        } else {
          params[x] = f
        }
      }
      return params
    }
  },
  methods: {
    updateCompact (v) {
      this.isCompact = v
    },
    saveView () {
      this.$dialog.prompt({
        message: this.$gettext('savedView.dialog.message'),
        confirmText: this.$gettext('savedView.dialog.confirm'),
        cancelText: this.$gettext('savedView.dialog.cancel'),
        inputAttrs: {
          maxlength: 50
        },
        onConfirm: (value) => {
          this.$store.dispatch('list/saveView', value)
          ToastSuccess(this.$gettext(`toast.savedView.save`))
        }
      })
    },
    doRefreshTable () {
      return api.methods
        .fetchList(this.schema.className, this.page - 1, this.perPage, this.sortField, this.sortOrder, this.filterParams)
        .then(({ data, meta }) => {
          this.data = []
          this.total = meta.totalElements
          this.$store.dispatch('list/changePage', meta.number + 1)
          data.forEach((item) => this.data.push(item))
          this.loading = false
        }, response => {
          this.data = []
          this.total = 0
          this.loading = false
        })
    },
    refreshTableAfterPaging: debounce(function () {
      return this.doRefreshTable()
    }, 500, {leading: true}),
    refreshTableAfterFilterUpdate: debounce(function () {
      return this.doRefreshTable()
    }, 500, {leading: false}),
    onPageChange (page) {
      this.loading = true
      this.$store
        .dispatch('list/changePage', page)
        .then(this.refreshTableAfterPaging)
    },
    updateSorting ({field, order}) {
      this.sortField = field
      this.sortOrder = order
      this.refreshTableAfterPaging()
    },
    print () {
      window.print()
    },
    updatePrintTimestamp () {
      this.printDate = this.currentDateString()
    }
  },
  beforeRouteEnter (to, from, next) {
    let resourceType = store.getters['list/getResourceType']
    if (to.params.resourceType == null && resourceType != null) {
      next({
        name: 'auflisten_resource',
        params: {
          resourceType: resourceType
        }
      })
    } else {
      next()
    }
  },
  mounted () {
    this.doRefreshTable()
    // init timestamp of print date
    this.updatePrintTimestamp()
    // update timestamp of print date after 30 sec
    setInterval(() => {
      this.updatePrintTimestamp()
    }, 30 * 1000)
  },
  watch: {
    resourceType () { this.$emit('updateHead') },
    currentLocale () { this.updatePrintTimestamp() }
  },
  components: {
    'fob-entity-list-options': EntityListOptions,
    'fob-entity-list-compact': EntityListCompact,
    'fob-entity-list-table': EntityListTable,
    'fob-entity-list-column-visibility': EntityListColumnVisibility,
    'fob-entity-palette': EntityPalette,
    'fob-entity-list-pagination': EntityListPagination
  }
}

</script>

<style>
  .card .level {
     margin-right: 30px;
  }
</style>



// WEBPACK FOOTER //
// src/components/EntityListView.vue